import {
    ServiceSessionDay,
    ServiceSession,
    SessionSpecificAssignment,
    SessionZone,
    WEEK_DAYS
} from 'model/ServiceSession';
import {
    SaveDefaultSessionsDto,
    SaveSpecificSessionsDto,
    ServiceSessionAssignmentDto,
    ServiceSessionCoverageDto,
    ServiceSessionDayDto,
    ServiceSessionDto,
    ServiceSessionsWeekDto
} from './ServiceSessionsDtos';

export const convertCoverage = (dto?: ServiceSessionCoverageDto | null): SessionZone | undefined => {
    if (!dto) return undefined;

    if (dto.type === 'zip_codes') {
        return {
            type: 'zip_codes',
            zipCodes: dto.zip_codes ?? []
        };
    }

    return {
        type: 'map',
        radius: dto.radius ?? 5,
        latitude: dto.latitude ?? undefined,
        longitude: dto.longitude ?? undefined,
        address: dto.address ?? undefined
    };
};

export const convertCoverageToDto = (zone?: SessionZone): ServiceSessionCoverageDto | undefined => {
    if (!zone) return undefined;

    if (zone.type === 'zip_codes') {
        return {
            type: 'zip_codes',
            zip_codes: zone.zipCodes
        };
    }

    return {
        type: 'map',
        radius: zone.radius,
        latitude: zone.latitude,
        longitude: zone.longitude,
        address: zone.address
    };
};

export const convertSession = (dto: ServiceSessionDto): ServiceSession => {
    const time = dto.start_time.slice(0, 5);
    return {
        id: dto.id,
        uuid: dto.uuid || `${dto.service_id}-${dto.staff_id}-${time}`,
        serviceId: dto.service_id,
        staffId: dto.staff_id,
        staffName: dto.staff_name,
        time,
        zone: convertCoverage(dto.coverage),
        recurrence: dto.recurrence,
        weekDay: dto.weekday === null || dto.weekday === undefined ? undefined : WEEK_DAYS[dto.weekday],
        sessionDate: dto.session_date ?? undefined
    };
};

export const convertDay = (dto: ServiceSessionDayDto): ServiceSessionDay => {
    return {
        date: dto.date,
        weekday: dto.weekday,
        sessions: (dto.sessions ?? []).map(convertSession),
        cancellations: (dto.cancellations ?? []).map(convertSession)
    };
};

export const convertWeek = (
    dto: ServiceSessionsWeekDto
): { startDate: string; endDate: string; days: Array<ServiceSessionDay> } => {
    return {
        startDate: dto.start_date,
        endDate: dto.end_date,
        days: (dto.days ?? []).map(convertDay)
    };
};

export const convertAssignmentToDto = (assignment: SessionSpecificAssignment): ServiceSessionAssignmentDto => {
    const dto: ServiceSessionAssignmentDto = {
        staff_id: assignment.staffId,
        start_time: assignment.time
    };

    if (assignment.active === false) {
        dto.active = false;
        return dto;
    }

    // Reviving an in-home session still needs coverage or the API answers 422.
    if (assignment.active === true) {
        dto.active = true;
    }

    // Coverage is sent even while In Home is off: the backend ignores it then, and leaving it
    // out would wipe the zone the session gets back when In Home is turned on again.
    const coverage = convertCoverageToDto(assignment.zone);
    if (coverage) dto.coverage = coverage;

    return dto;
};

export const convertDefaultSaveToDto = (
    serviceId: number,
    weekdays: Array<number>,
    assignments: Array<SessionSpecificAssignment>
): SaveDefaultSessionsDto => {
    return {
        service_id: serviceId,
        weekdays,
        assignments: assignments.map(assignment => convertAssignmentToDto(assignment))
    };
};

export const convertSpecificSaveToDto = (
    serviceId: number,
    sessionDate: string,
    assignments: Array<SessionSpecificAssignment>
): SaveSpecificSessionsDto => {
    return {
        service_id: serviceId,
        session_date: sessionDate,
        assignments: assignments.map(assignment => convertAssignmentToDto(assignment))
    };
};
